import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryOptions } from '@cast/shared-types';
import {
  DboAdvisorRecommendationDetail,
  DboRecommendationKind,
} from '@cast/types-database-optimizer';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const apiFn = apiClient.dbo.getPerformanceAdvisorRecommendation;

export type PerformanceAdvisorRecommendationQueryData =
  DboAdvisorRecommendationDetail;

export type PerformanceAdvisorRecommendationQueryArgs<SELECT> = QueryOptions<
  PerformanceAdvisorRecommendationQueryData,
  SELECT
> & {
  databaseId?: string;
  recommendationId?: string;
};

const DBO_PERFORMANCE_ADVISOR_RECOMMENDATION_QUERY_KEY =
  'dbo-performance-advisor-recommendation';

export const getPerformanceAdvisorRecommendationQueryKey = ({
  databaseId,
  recommendationId,
}: {
  databaseId?: string;
  recommendationId?: string;
}) =>
  [
    DBO_PERFORMANCE_ADVISOR_RECOMMENDATION_QUERY_KEY,
    databaseId,
    recommendationId,
  ] as const;

const getPerformanceAdvisorRecommendationQueryOptions = ({
  databaseId,
  recommendationId,
  organizationId,
  enabled,
}: {
  databaseId?: string;
  recommendationId?: string;
  organizationId: string;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getPerformanceAdvisorRecommendationQueryKey({
      databaseId,
      recommendationId,
    }),
    queryFn: async () => {
      const { data } = await apiFn({
        organizationId,
        databaseId: databaseId!,
        id: recommendationId!,
      });
      // @ts-expect-error - TODO(VytautasM): this will start failing if BE updates the response and
      // `kind` is already on the recommendation object. If so, remove the entire assignment.
      data.recommendation.kind = data.recommendation.indexDetails
        ? DboRecommendationKind.INDEX
        : DboRecommendationKind.QUERY;
      return data as DboAdvisorRecommendationDetail;
    },
    enabled: makeIsEnabled(!!databaseId, !!recommendationId, enabled),
  });

export const usePerformanceAdvisorRecommendationQuery = <
  SELECT = PerformanceAdvisorRecommendationQueryData
>({
  databaseId,
  recommendationId,
  enabled,
  refetchInterval,
}: PerformanceAdvisorRecommendationQueryArgs<SELECT>) => {
  const { currentOrganization } = useOrganizations();

  return useQuery({
    ...getPerformanceAdvisorRecommendationQueryOptions({
      databaseId,
      recommendationId,
      organizationId: currentOrganization!.id,
      enabled,
    }),
    refetchInterval,
  });
};
